import React, { useState, useEffect, useCallback } from 'react';
import { Receipt } from '../../../../../../../Caja/types';
import { getPaymentReciptsByLocalIdAndDateRange } from '../../../../../../../../../crud/payments_api/payment_receipts';
import { toast } from 'react-toastify';
import LoadingSpinner from '../../../../../../../../../components/common/LoadingSpinner';
import { formatCurrency } from '../../../../../../../../../utils/functions';
import ReceiptDetails from '../../../../../../../Caja/Receipts/components/Table/components/ReceiptDetails/ReceiptDetails';
import CheckboxMultiSelect from '../../../../../../../../../components/common/CheckboxMultiSelect/CheckboxMultiSelect';
import './Recibos.scss';

interface RecibosProps {
  isActive: boolean;
}

const Recibos: React.FC<RecibosProps> = ({ isActive }) => {
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [filteredReceipts, setFilteredReceipts] = useState<Receipt[]>([]);
  const [loading, setLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState<Receipt | null>(null);
  const [showModal, setShowModal] = useState(false);

  // Filter states
  const [selectedPedidosType, setSelectedPedidosType] = useState<{value: any, label: string}[]>([]);
  const [selectedMesas, setSelectedMesas] = useState<{value: any, label: string}[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<{value: any, label: string}[]>([]);

  // Filter options
  const [pedidosTypeOptions, setPedidosTypeOptions] = useState<{value: any, label: string}[]>([]);
  const [mesaOptions, setMesaOptions] = useState<{value: any, label: string}[]>([]);
  const [userOptions, setUserOptions] = useState<{value: any, label: string}[]>([]);

  const fetchReceipts = useCallback(async () => {
    setLoading(true);
    const userContext = localStorage.getItem('user');
    const localId = userContext ? JSON.parse(userContext).local_id : 0;

    // Get today's receipts only
    const today = new Date();
    const startDate = today.toISOString().split('T')[0];
    const endDate = today.toISOString().split('T')[0];

    try {
      const response = await getPaymentReciptsByLocalIdAndDateRange(localId, startDate, endDate);
      const data: Receipt[] = response.data;
      
      setReceipts(data);
      setFilteredReceipts(data);
      setDataLoaded(true);

      // Extract unique filter options
      extractFilterOptions(data);
    } catch (error) {
      console.error('Error loading receipts:', error);
      toast.error('Error cargando recibos');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch receipts when component becomes active
  useEffect(() => {
    if (isActive && !dataLoaded) {
      fetchReceipts();
    }
  }, [isActive, dataLoaded, fetchReceipts]);

  const extractFilterOptions = (data: Receipt[]) => {
    // Extract pedido types - we need to map them somehow
    const typesMap = new Map<number, string>();
    const typeNames: { [key: number]: string } = {
      1: 'Restaurant',
      2: 'Delivery',
      3: 'Retiro en local',
      4: 'Venta Caja',
      5: 'Mesa Virtual',
    };
    
    data.forEach(r => {
      if (r.pedido_type_id) {
        const name = typeNames[r.pedido_type_id] || `Tipo ${r.pedido_type_id}`;
        typesMap.set(r.pedido_type_id, name);
      }
    });
    setPedidosTypeOptions(
      Array.from(typesMap.entries())
        .map(([id, name]) => ({ value: id, label: name }))
    );

    // Extract mesas
    const mesasMap = new Map<number, string>();
    data.forEach(r => {
      if (r.mesa_id && r.mesa_num) {
        mesasMap.set(r.mesa_id, r.mesa_num);
      }
    });
    setMesaOptions(
      Array.from(mesasMap.entries()).map(([id, num]) => ({ value: id, label: `Mesa ${num}` }))
    );

    // Extract users
    const usersMap = new Map<number, string>();
    data.forEach(r => {
      if (r.user_id && r.user_names && r.user_names.length > 0) {
        usersMap.set(r.user_id, r.user_names[0]);
      }
    });
    setUserOptions(
      Array.from(usersMap.entries())
        .filter(([_, name]) => name !== 'Ninguno')
        .map(([id, name]) => ({ value: id, label: name }))
    );
  };

  // Apply filters
  useEffect(() => {
    let filtered = receipts;

    if (selectedPedidosType.length > 0) {
      const ids = selectedPedidosType.map(p => p.value);
      filtered = filtered.filter(r => ids.includes(r.pedido_type_id));
    }

    if (selectedMesas.length > 0) {
      const ids = selectedMesas.map(m => m.value);
      filtered = filtered.filter(r => ids.includes(r.mesa_id));
    }

    if (selectedUsers.length > 0) {
      const ids = selectedUsers.map(u => u.value);
      filtered = filtered.filter(r => ids.includes(r.user_id));
    }

    setFilteredReceipts(filtered);
  }, [receipts, selectedPedidosType, selectedMesas, selectedUsers]);

  const handleOpenModal = useCallback((receipt: Receipt) => {
    setShowModal(false);
    setSelectedReceipt(null);
    
    setTimeout(() => {
      setSelectedReceipt(receipt);
      setShowModal(true);
    }, 10);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setSelectedReceipt(null);
  }, []);

  const getReceiptStatusInfo = (receipt: Receipt) => {
    if (receipt.is_refunded) {
      return { className: 'reembolsed', text: 'Reembolsado' };
    } else if (receipt.is_cancelled) {
      return { className: 'cancelled', text: 'Cancelado' };
    } else if (receipt.payed_full) {
      return { className: 'success', text: 'Pagado' };
    } else if (receipt.active) {
      return { className: 'warning', text: 'Pendiente' };
    } else {
      return { className: 'unactive', text: 'Inactivo' };
    }
  };

  const formatDateOrTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
  };

  if (!isActive) {
    return null;
  }

  if (loading && !dataLoaded) {
    return (
      <div className="recibos-panel">
        <LoadingSpinner text="Cargando recibos..." transparent />
      </div>
    );
  }

  return (
    <div className="recibos-panel">
      {/* Filters Section */}
      <div className="recibos-filters">
        <div className="filter-item">
          <CheckboxMultiSelect
            options={pedidosTypeOptions}
            value={selectedPedidosType}
            onChange={setSelectedPedidosType}
            placeholder="Tipo"
          />
        </div>
        <div className="filter-item">
          <CheckboxMultiSelect
            options={mesaOptions}
            value={selectedMesas}
            onChange={setSelectedMesas}
            placeholder="Mesa"
          />
        </div>
        <div className="filter-item">
          <CheckboxMultiSelect
            options={userOptions}
            value={selectedUsers}
            onChange={setSelectedUsers}
            placeholder="Usuario"
          />
        </div>
      </div>

      {/* Receipts List */}
      <div className="recibos-list">
        {filteredReceipts.length === 0 ? (
          <div className="recibos-empty">
            <p>No hay recibos para mostrar</p>
          </div>
        ) : (
          filteredReceipts.map(receipt => {
            const statusInfo = getReceiptStatusInfo(receipt);
            
            return (
              <div 
                key={receipt.receipt_id} 
                className="recibo-card"
                onClick={() => handleOpenModal(receipt)}
              >
                {/* Row 1: User name and status badge */}
                <div className="recibo-card-row-1">
                  <div className="recibo-card-user">
                    {receipt.user_names ? receipt.user_names[0] : 'N/A'}
                  </div>
                  <div className={`table-cell-status ${statusInfo.className}`}>
                    {statusInfo.text}
                  </div>
                </div>

                {/* Row 2: Time, Mesa/Client tags, and Total */}
                <div className="recibo-card-row-2">
                  <div className="recibo-card-left-info">
                    <div className="recibo-card-time">
                      {formatDateOrTime((receipt.local_timestamp ?? receipt.timestamp)!)}
                    </div>
                    {receipt.mesa_num && (
                      <div className="recibo-card-tag mesa-tag">
                        Mesa {receipt.mesa_num}
                      </div>
                    )}
                    {receipt.client_name && (
                      <div className="recibo-card-tag client-tag">
                        {receipt.client_name}
                      </div>
                    )}
                  </div>
                  <div className="recibo-card-total">
                    {formatCurrency(receipt.total_due)}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Receipt Details Modal */}
      {selectedReceipt && showModal && (
        <ReceiptDetails
          show={showModal}
          onClose={handleCloseModal}
          currentReceipt={selectedReceipt}
          setReceipts={setReceipts}
        />
      )}
    </div>
  );
};

export default Recibos;
